// frontend/src/context/AuthContext.tsx
/**
 * AUTH CONTEXT
 *
 * Centralized auth state and actions using Hey API DMS client.
 *
 * 1. **State Management**: Manages user session state, tokens, loading states
 * 2. **Clean Auth Actions**: Provides simple methods for all auth operations
 * 3. **Hey API SDK**: All operations use generated SDK functions
 *
 * USAGE PATTERNS:
 * ```typescript
 * const { login, logout, resetPassword, enable2FA } = useAuth();
 * await login({ email, password });
 * ```
 *
 * Auth tokens are handled by the shared axios interceptor — no need to
 * pass tokens to SDK calls.
 */

import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import {
  acceptInvitation as acceptInvitationSdk,
  changePassword as changePasswordSdk,
  disable2Fa,
  enable2Fa,
  generate2Fa,
  getProfile,
  login2Fa,
  login as loginSdk,
  logout as logoutSdk,
  passwordRecovery,
  passwordReset as passwordResetSdk,
  updateProfile as updateProfileSdk,
} from '@/api/core/sdk.gen';
import type {
  AcceptInvitationRequest,
  ChangePasswordRequest,
  LoginRequest,
  LoginResponse,
  PasswordRecoveryRequest,
  PasswordResetConfirm,
  ProfileResponse,
  TotpCodeRequest,
  TwoFactorRequest,
  UpdateProfileRequest,
  UserProfileResponse,
  UserResponse,
} from '@/api/core/types.gen';

// Type aliases for backward compatibility
type TwoFactorEnable = TotpCodeRequest;
type TwoFactorDisable = TotpCodeRequest;

import { parseApiError } from '@/hooks/useApiErrorHandler';

// --- Type Definitions ---
interface AuthContextType {
  // State
  token: string | null;
  user: ProfileResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Auth Actions
  login: (
    credentials: Omit<LoginRequest, 'remember_me'> & { remember_me?: boolean }
  ) => Promise<LoginResponse>;
  loginWith2FA: (data: TwoFactorRequest) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  requestPasswordReset: (data: PasswordRecoveryRequest) => Promise<void>;
  resetPassword: (data: PasswordResetConfirm) => Promise<void>;
  acceptInvitation: (data: AcceptInvitationRequest) => Promise<UserResponse>;

  // User Profile Actions
  updateProfile: (data: UpdateProfileRequest) => Promise<UserProfileResponse>;
  updatePassword: (data: ChangePasswordRequest) => Promise<void>;
  generate2FASecret: () => Promise<{ uri: string; secret: string }>;
  enable2FA: (data: TwoFactorEnable) => Promise<void>;
  disable2FA: (data: TwoFactorDisable) => Promise<void>;

  // Utility Methods
  refreshUser: () => Promise<void>;
  setAuthenticatedSession: (token: string, user: ProfileResponse | UserProfileResponse) => void;

  // All features use Hey API SDK
}

// --- Context Creation ---
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// --- AuthProvider Component ---
export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('authToken'));
  const queryClient = useQueryClient();

  // === CLEAN AUTH ACTION METHODS ===

  const setAuthenticatedSession = useCallback(
    (newToken: string, fetchedUser: ProfileResponse | UserProfileResponse) => {
      localStorage.setItem('authToken', newToken);
      // Seed cache for immediate use (no loading flash)
      queryClient.setQueryData(['currentUser'], fetchedUser);
      setToken(newToken);
      // Login response has UserResponse (missing has_passkeys, totp_enabled).
      // Trigger background refetch to get full ProfileResponse.
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    [queryClient]
  );

  const login = useCallback(
    async (credentials: Omit<LoginRequest, 'remember_me'> & { remember_me?: boolean }) => {
      const { data } = await loginSdk({
        body: {
          email: credentials.email,
          password: credentials.password,
          remember_me: credentials.remember_me ?? false,
        },
        throwOnError: true,
      });

      // If login successful and no 2FA required, set session
      if ('two_factor_required' in data && (data as any).two_factor_required === false) {
        const loginData = data as any; // Type assertion for union type
        if (loginData.login_success && loginData.user) {
          setAuthenticatedSession(loginData.token, loginData.user);
        }
      }

      return data;
    },
    [setAuthenticatedSession]
  );

  const loginWith2FA = useCallback(
    async (body: TwoFactorRequest) => {
      const { data } = await login2Fa({ body, throwOnError: true });

      // Set session after successful 2FA
      const loginData = data as any; // Type assertion for union type
      if (loginData.login_success && loginData.user) {
        setAuthenticatedSession(loginData.token, loginData.user);
      }

      return data;
    },
    [setAuthenticatedSession]
  );

  const logout = useCallback(async () => {
    const currentToken = localStorage.getItem('authToken');
    if (currentToken) {
      try {
        await logoutSdk({ throwOnError: true });
      } catch (error) {
        console.error(
          'Server logout failed, proceeding with client-side logout:',
          parseApiError(error).message
        );
      }
    }
    localStorage.removeItem('authToken');
    setToken(null);
    queryClient.clear();
    Sentry.setUser(null);
  }, [queryClient]);

  const requestPasswordReset = useCallback(async (body: PasswordRecoveryRequest) => {
    await passwordRecovery({ body, throwOnError: true });
  }, []);

  const resetPassword = useCallback(async (body: PasswordResetConfirm) => {
    await passwordResetSdk({ body, throwOnError: true });
  }, []);

  const acceptInvitation = useCallback(async (body: AcceptInvitationRequest) => {
    const { data } = await acceptInvitationSdk({ body, throwOnError: true });
    return data;
  }, []);

  const updateProfile = useCallback(
    async (body: UpdateProfileRequest) => {
      if (!token) throw new Error('Not authenticated');
      const { data } = await updateProfileSdk({ body, throwOnError: true });

      // Update cached user data
      queryClient.setQueryData(['currentUser'], data);

      return data;
    },
    [token, queryClient]
  );

  const updatePassword = useCallback(
    async (body: ChangePasswordRequest) => {
      if (!token) throw new Error('Not authenticated');
      await changePasswordSdk({ body, throwOnError: true });
    },
    [token]
  );

  const generate2FASecret = useCallback(async () => {
    if (!token) throw new Error('Not authenticated');
    const { data } = await generate2Fa({ throwOnError: true });
    return data;
  }, [token]);

  const enable2FA = useCallback(
    async (body: TwoFactorEnable) => {
      if (!token) throw new Error('Not authenticated');
      await enable2Fa({ body, throwOnError: true });

      // Refresh user data to reflect 2FA status
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    [token, queryClient]
  );

  const disable2FA = useCallback(
    async (body: TwoFactorDisable) => {
      if (!token) throw new Error('Not authenticated');
      await disable2Fa({ body, throwOnError: true });

      // Refresh user data to reflect 2FA status
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    [token, queryClient]
  );

  // NEW: Fetch the current user with useQuery
  const {
    data: user,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['currentUser'], // Unique key for the logged-in user
    queryFn: async () => {
      if (!token) return null; // If no token, don't fetch
      const { data } = await getProfile({ throwOnError: true });
      return data;
    },
    enabled: !!token, // Only run this query if a token exists
    staleTime: 1000 * 60 * 15, // User data can be cached for 15 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401 Unauthorized errors, as it means the token is invalid
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return false;
      }
      return failureCount < 3; // Retry other errors up to 3 times
    },
  });

  // Effect to handle invalid tokens (e.g., expired)
  useEffect(() => {
    if (isError) {
      console.error('Failed to fetch user, logging out.');
      logout();
    }
  }, [isError, logout]);

  // Set Sentry user context when user data is available
  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  const refreshUser = async () => {
    // Invalidation is the TanStack Query way of refreshing
    await queryClient.invalidateQueries({ queryKey: ['currentUser'] });
  };

  // User is authenticated if we have both token and user data
  // OR if we have a token and are still loading (prevents redirect during initial load)
  const isAuthenticated = !!token && (!!user || isLoading);

  const value = {
    // State
    token,
    user: user ?? null,
    isAuthenticated,
    isLoading,

    // Auth Actions
    login,
    loginWith2FA,
    logout,
    requestPasswordReset,
    resetPassword,
    acceptInvitation,

    // User Profile Actions
    updateProfile,
    updatePassword,
    generate2FASecret,
    enable2FA,
    disable2FA,

    // Utility Methods
    refreshUser,
    setAuthenticatedSession,

    // All features use Hey API SDK
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
